import React, { useState, useEffect, useRef, DragEvent, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import Layout from '../../components/Layout';
import BopBackLink from '../../components/BopBackLink';
import { apiFetch, apiDownload } from '../../lib/api';
import BACKEND_URL from '../../config.js';
import '../../styles/aziendale.css';

interface Versione {
  id: number;
  versione: number;
  pathGcs: string;
  nomeFile: string;
  dataScadenza: string;
  uploadedAt: string;
  note: string | null;
  uploadedBy: { id: number; nome: string };
}

interface Remind {
  id: number;
  versioneId: number;
  versione_num: number;
  tipoRemind: 'PREAVVISO' | 'SCADENZA';
  schedulatoAt: string;
  inviatoAt: string | null;
  esito: string | null;
}

interface Documento {
  id: number;
  nome: string;
  societaId: number;
  societa: { id: number; nome: string };
  responsabile: { id: number; nome: string; email: string };
  giorniPreavviso: number;
  attivo: boolean;
  createdAt: string;
  updatedAt: string;
  statoScadenza: 'VERDE' | 'GIALLO' | 'ROSSO';
}

const BADGE: Record<string, { bg: string; color: string; label: string }> = {
  VERDE: { bg: '#dcfce7', color: '#16a34a', label: 'In scadenza OK' },
  GIALLO: { bg: '#fef9c3', color: '#854d0e', label: 'In preavviso' },
  ROSSO: { bg: '#fef2f2', color: '#dc2626', label: 'Scaduto' },
};


export default function DettaglioDocumentoRicorrente() {
  const { id } = useParams<{ id: string }>();

  const [documento, setDocumento] = useState<Documento | null>(null);
  const [versioni, setVersioni] = useState<Versione[]>([]);
  const [remind, setRemind] = useState<Remind[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Modal Nuova Versione
  const [showModalVer, setShowModalVer] = useState(false);
  const [verDataScadenza, setVerDataScadenza] = useState('');
  const [verNote, setVerNote] = useState('');
  const [verFile, setVerFile] = useState<File | null>(null);
  const [isSavingVer, setIsSavingVer] = useState(false);
  const fileVerRef = useRef<HTMLInputElement>(null);

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await apiFetch<{ documento: Documento; versioni: Versione[]; remind: Remind[] }>(
        `/api/documenti-ricorrenti/${id}`
      );
      setDocumento(data.documento);
      setVersioni(data.versioni);
      setRemind(data.remind);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDragVer = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (e.dataTransfer.files[0]) setVerFile(e.dataTransfer.files[0]);
  };

  const handleSubmitVer = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!verFile) { alert('Seleziona un file'); return; }
    if (!verDataScadenza) { alert('Inserisci la data di scadenza'); return; }
    try {
      setIsSavingVer(true);
      const formData = new FormData();
      formData.append('dataScadenza', verDataScadenza);
      formData.append('note', verNote);
      formData.append('file', verFile);
      const res = await fetch(`${BACKEND_URL}/api/documenti-ricorrenti/${id}/versioni`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: formData,
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.error || 'Errore'); }
      setShowModalVer(false);
      setVerDataScadenza(''); setVerNote(''); setVerFile(null);
      if (fileVerRef.current) fileVerRef.current.value = '';
      fetchData();
    } catch (err: any) {
      alert(err.message);
    } finally {
      setIsSavingVer(false);
    }
  };

  if (isLoading) {
    return <Layout title="Dettaglio Documento"><div className="aziendale-loading">Caricamento...</div></Layout>;
  }
  if (!documento) {
    return <Layout title="Dettaglio Documento"><div className="aziendale-loading bop-text-danger">Documento non trovato.</div></Layout>;
  }

  const badge = BADGE[documento.statoScadenza];
  const ultimaVersione = versioni[0] || null;

  return (
    <Layout title={documento.nome}>
      {/* ── Torna indietro ── */}
      <BopBackLink to="/aziendale/documenti-ricorrenti" label="Torna ai documenti ricorrenti" />

      {/* ── Header documento ── */}
      <div className="bop-card">
        <div className="aziendale-doc-header">
          <div className="aziendale-doc-header-left">
            <h1 className="aziendale-doc-title">{documento.nome}</h1>
            <div className="aziendale-doc-meta">
              <span><strong>Società:</strong> {documento.societa.nome}</span>
              <span><strong>Responsabile:</strong> {documento.responsabile.nome} ({documento.responsabile.email})</span>
              <span><strong>Preavviso:</strong> {documento.giorniPreavviso} giorni</span>
            </div>
          </div>
          <span className={`aziendale-doc-badge-status aziendale-doc-badge-status--${documento.statoScadenza.toLowerCase()}`}>
            {badge.label}
          </span>
        </div>
      </div>

      {/* ── Versione Corrente ── */}
      <div className="bop-card">
        <div className="aziendale-version-header">
          <h2 className="aziendale-section-title">Versione Corrente</h2>
          <button onClick={() => { setVerDataScadenza(''); setVerNote(''); setVerFile(null); setShowModalVer(true); }} className="aziendale-btn-carica-versione">
            + Carica Nuova Versione
          </button>
        </div>
        {ultimaVersione ? (
          <div className="aziendale-version-grid">
            <div className="aziendale-version-stat">
              <div className="aziendale-version-stat-label">VERSIONE</div>
              <div className="aziendale-version-stat-value">v{ultimaVersione.versione}</div>
            </div>
            <div className="aziendale-version-stat">
              <div className="aziendale-version-stat-label">SCADENZA</div>
              <div className={`aziendale-version-stat-value-md aziendale-version-stat-value-md--${documento.statoScadenza.toLowerCase()}`}>
                {new Date(ultimaVersione.dataScadenza).toLocaleDateString('it-IT')}
              </div>
            </div>
            <div className="aziendale-version-stat">
              <div className="aziendale-version-stat-label">FILE</div>
              <div className="aziendale-version-stat-value-sm">{ultimaVersione.nomeFile}</div>
            </div>
            {ultimaVersione.note && (
              <div className="aziendale-version-stat aziendale-version-stat-full">
                <div className="aziendale-version-stat-label">NOTE</div>
                <div className="aziendale-version-stat-value-sm">{ultimaVersione.note}</div>
              </div>
            )}
            <div className="aziendale-version-download-wrapper">
              {/* @api GET /api/documenti-ricorrenti/versioni/:versioneId/download -> file stream (attachment) */}
              <button
                type="button"
                className="aziendale-version-link-download"
                onClick={() => apiDownload(`/api/documenti-ricorrenti/versioni/${ultimaVersione.id}/download`, ultimaVersione.nomeFile || 'documento')}
              >
                Scarica PDF
              </button>
            </div>
          </div>
        ) : (
          <div className="aziendale-version-empty">Nessuna versione caricata.</div>
        )}
      </div>

      {/* ── Storico Versioni ── */}
      <div className="bop-card">
        <h2 className="aziendale-section-title aziendale-section-title-spaced bop-mt-0">Storico Versioni</h2>
        {versioni.length === 0 ? (
          <div className="aziendale-version-empty">Nessuna versione disponibile.</div>
        ) : (
          <div className="aziendale-table-wrapper">
            <table className="aziendale-table-version">
              <thead>
                <tr>
                  {['Versione', 'Data Upload', 'Caricato da', 'Scadenza', 'Note', 'Download'].map(h => (
                    <th key={h}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {versioni.map(v => (
                  <tr key={v.id}>
                    <td className="aziendale-table-version-bold">v{v.versione}</td>
                    <td className="aziendale-table-version-date">{new Date(v.uploadedAt).toLocaleDateString('it-IT')}</td>
                    <td className="aziendale-table-version-date">{v.uploadedBy.nome}</td>
                    <td className="aziendale-table-cell-secondary">{new Date(v.dataScadenza).toLocaleDateString('it-IT')}</td>
                    <td className="aziendale-table-version-note">
                      {v.note ? <span title={v.note}>{v.note.length > 60 ? v.note.substring(0, 60) + '…' : v.note}</span> : <em className="aziendale-table-cell-em">—</em>}
                    </td>
                    <td>
                      {/* @api GET /api/documenti-ricorrenti/versioni/:versioneId/download -> file stream (attachment) */}
                      <button
                        type="button"
                        className="aziendale-table-version-link"
                        onClick={() => apiDownload(`/api/documenti-ricorrenti/versioni/${v.id}/download`, v.nomeFile || 'documento')}
                      >
                        Scarica
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Remind Programmati ── */}
      <div className="bop-card">
        <h2 className="aziendale-section-title aziendale-section-title-spaced bop-mt-0">Remind Programmati</h2>
        {remind.length === 0 ? (
          <div className="aziendale-version-empty">Nessun remind programmato.</div>
        ) : (
          <div className="aziendale-table-wrapper">
            <table className="aziendale-table-version">
              <thead>
                <tr>
                  {['Tipo', 'Versione', 'Programmato il', 'Stato', 'Inviato il', 'Esito'].map(h => (
                    <th key={h}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {remind.map(r => {
                  const isInviato = !!r.inviatoAt;
                  const isSaltato = r.esito?.startsWith('SALTATO');
                  const isErrore = r.esito?.startsWith('ERRORE');
                  let statusClass = 'aziendale-remind-status-attesa';
                  let statoLabel = 'In attesa';
                  if (isInviato && !isSaltato && !isErrore) { statusClass = 'aziendale-remind-status-inviato'; statoLabel = 'Inviato'; }
                  else if (isSaltato) { statusClass = 'aziendale-remind-status-saltato'; statoLabel = 'Saltato'; }
                  else if (isErrore) { statusClass = 'aziendale-remind-status-errore'; statoLabel = 'Errore'; }
                  return (
                    <tr key={r.id}>
                      <td>
                        <span className={`aziendale-remind-badge ${r.tipoRemind === 'PREAVVISO' ? 'aziendale-remind-badge-preavviso' : 'aziendale-remind-badge-scadenza'}`}>
                          {r.tipoRemind}
                        </span>
                      </td>
                      <td className="aziendale-table-version-date">v{r.versione_num}</td>
                      <td className="aziendale-table-cell-secondary">{new Date(r.schedulatoAt).toLocaleDateString('it-IT')}</td>
                      <td>
                        <span className={`aziendale-remind-badge ${statusClass}`}>{statoLabel}</span>
                      </td>
                      <td className="aziendale-table-version-date">
                        {r.inviatoAt ? new Date(r.inviatoAt).toLocaleDateString('it-IT') : <em className="aziendale-table-cell-em">—</em>}
                      </td>
                      <td className="aziendale-table-version-note">
                        {r.esito ? <span title={r.esito}>{r.esito.length > 50 ? r.esito.substring(0, 50) + '…' : r.esito}</span> : <em className="aziendale-table-cell-em">—</em>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Modal: Nuova Versione ── */}
      {showModalVer && (
        <div className="aziendale-modal-overlay">
          <div className="aziendale-modal aziendale-modal-md">
            <div className="aziendale-modal-header aziendale-modal-header-compact">
              <h2 className="aziendale-modal-title">Nuova Versione</h2>
              <button onClick={() => setShowModalVer(false)} className="aziendale-modal-close">×</button>
            </div>
            <p className="aziendale-modal-subtitle">
              <strong>{documento.nome}</strong> — versione corrente: {ultimaVersione ? `v${ultimaVersione.versione}` : 'nessuna'}
            </p>
            <form onSubmit={handleSubmitVer} className="aziendale-modal-form">
              <div className="aziendale-form-field">
                <label className="aziendale-form-label">Nuova data scadenza *</label>
                <input type="date" value={verDataScadenza} onChange={e => setVerDataScadenza(e.target.value)} className="aziendale-form-input" required />
              </div>
              <div className="aziendale-form-field">
                <label className="aziendale-form-label">File *</label>
                <div onDragOver={e => e.preventDefault()} onDrop={handleDragVer}
                  onClick={() => fileVerRef.current?.click()}
                  className={verFile ? 'aziendale-file-dropzone aziendale-file-dropzone-active' : 'aziendale-file-dropzone'}>
                  <input type="file" ref={fileVerRef} className="bop-sr-only" onChange={e => e.target.files && setVerFile(e.target.files[0])} />
                  {verFile ? (
                    <span className="aziendale-file-selected">{verFile.name}</span>
                  ) : (
                    <span className="aziendale-file-prompt"><span className="aziendale-file-prompt-link">Clicca</span> o trascina il file qui</span>
                  )}
                </div>
              </div>
              <div className="aziendale-form-field">
                <label className="aziendale-form-label">Note (opzionale)</label>
                <textarea value={verNote} onChange={e => setVerNote(e.target.value)} className="aziendale-form-textarea" placeholder="Note sulla versione..." />
              </div>
              <div className="aziendale-modal-footer">
                <button type="button" onClick={() => setShowModalVer(false)} className="aziendale-btn-cancel">
                  Annulla
                </button>
                <button type="submit" disabled={isSavingVer} className="aziendale-btn-submit">
                  {isSavingVer ? 'Caricamento...' : 'Carica Nuova Versione'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}
